import { scaleLog } from "d3";
import VariantDBDetailsFrequency from "../../../../models/VariantDBDetailsFrequency";
import Variant from "../../../../models/Variant";
import flatten from "../../helpers/flatten";
import {
  getAdditionMesh,
  getSelectionMesh,
  getVariantMesh,
  getDeletionMesh,
  updateVis,
  drawVariant,
  drawSelection,
  colorMap,
} from "./FreqBlueprint";
import { getSelectionColorMap } from "../../constants/colors";
import partition from "../../../../common/helpers/partition";
import { frequencyDbs } from "../../constants/dbs";
import clamp from "../../../../common/helpers/clamp";
import getFilters from "./getFreqFilters";
import getFrequencyTicks from "./getFrequencyTicks";

const dbs = Object.values(frequencyDbs);

function getDefaultState() {
  return {
    config: {},
    selectedVariantId: null,
    //
    allVariants: [],
    substitutions: [],
    additions: [],
    deletions: [],
    selections: [],
    //
    filteredData: [],
    domain: [0, 0],
    variantDict: {},
    filters: getFilters(),
  };
}

function getVariantsTrackController(track) {
  let state = getDefaultState();
  const substitutionMesh = getVariantMesh();
  const additionMesh = getAdditionMesh();
  const deletionMesh = getDeletionMesh();
  const selectionsMesh = getSelectionMesh();

  const positionScale = scaleLog()
    .domain([10 ** -6, 1])
    .range([0, 1]);

  function onReset() {
    state = getDefaultState();
  }

  function onInit() {
    track.setFilters([...state.filters]);
    adjustAxis();
  }

  function onRangeChange({ chrSegments }) {
    track.loadPosition(chrSegments);
  }

  function processNewVariants(res, chr) {
    const dict = Object.entries(res)
      .filter(([dbName]) => frequencyDbs[dbName])
      .map(([dbName, dbVersions]) => {
        return dbVersions
          .filter((v) => Array.isArray(v?.variants))
          .map(({ variants }) => {
            const db = frequencyDbs[dbName];
            return variants
              .map((v) => {
                const existing = track.variantRegistry.get(v.variant_id);
                const variant = existing || new Variant(v, chr);
                if (!existing) track.variantRegistry.set(variant.id, variant);
                return new VariantDBDetailsFrequency(v, variant, db);
              })
              .filter((x) => x.freq !== null);
          });
      });

    return flatten(flatten(dict));
  }

  function deduplicate(entries) {
    return entries.filter((v) => {
      if (state.variantDict[v.id]) return false;
      state.variantDict[v.id] = v;
      return true;
    });
  }

  function partitionVariants(variants = []) {
    const isAddition = (x) => x.variant.refLength === 0;
    const isDeletion = (x) => x.variant.altLength === 0;
    const [subs, adds, dels] = partition(variants, isAddition, isDeletion);
    return { subs, adds, dels };
  }

  function onDataReceived({ res, chr }) {
    const newVars = processNewVariants(res, chr);
    const uniqueVars = deduplicate(newVars);
    if (uniqueVars.length === 0) return;
    const { subs, adds, dels } = partitionVariants(uniqueVars);
    const activeFilters = getActiveFilters();
    const hasFilters = Boolean(activeFilters.length);
    state.allVariants = state.allVariants.concat(uniqueVars);
    state.substitutions = state.substitutions.concat(subs);
    state.deletions = state.deletions.concat(dels);
    state.additions = state.additions.concat(adds);

    applyFiltersToVariants(uniqueVars, activeFilters);

    substitutionMesh.setItems(state.substitutions);
    deletionMesh.setItems(state.deletions);
    additionMesh.setItems(state.additions);

    drawVariant(subs, substitutionMesh, hasFilters, 0.05);
    drawVariant(dels, deletionMesh, hasFilters, 0.05);
    drawVariant(adds, additionMesh, hasFilters, 0);

    updateSelection();
    calcFilters();
    update();
  }

  function updateColorMap(highlight) {
    const { config } = state;
    const vec = getSelectionColorMap(config.isDark);
    const { vectors } = colorMap.calc(config, highlight);
    const noHighlightColorMap = colorMap.calc(config);

    substitutionMesh.uniforms.setColorMap(vectors);
    deletionMesh.uniforms.setColorMap(vectors);
    additionMesh.uniforms.setColorMap(vectors);
    selectionsMesh.uniforms.setColorMap([vec]);
    state.filters.forEach((x) => {
      x.setColor(noHighlightColorMap.getKey(x.key)?.color?.hex);
    });
    track.setFilters([...state.filters]);
    update();
  }

  function onPreferencesChanged() {
    state.config = track.getPreferences();
    updateColorMap();
  }

  function update() {
    track.requestRender();
  }

  function calcFilters() {
    const maxFilter = state.filters.find((x) => x.key === "freqMax");
    const dbFilters = state.filters.filter((x) => frequencyDbs[x.key]);
    const max = maxFilter.value || 1;
    const maxRange = VariantDBDetailsFrequency.valToFreqPosition(max);
    const dictCountDict = dbFilters
      .map(({ key }) => key)
      .reduce((a, i) => ({ ...a, [i]: 0 }), {});

    for (let i = 0; i < state.allVariants.length; i++) {
      const x = state.allVariants[i];
      if (x.freq <= maxRange) {
        dictCountDict[x.db.key] += 1;
      }
    }

    dbFilters.forEach((x) => {
      x.totalCount = dictCountDict[x.key];
    });

    track.setFilters([...dbFilters, maxFilter]);
  }

  function adjustAxis() {
    const freqMax = state.filters.find((x) => x.key === "freqMax");
    const { value: max } = freqMax;
    const min = 10 ** -6;
    const fixedMax = clamp(max, 10 ** -6, 1);

    track.setAxisProps({ ticks: getFrequencyTicks(min, fixedMax) });

    const minY = positionScale(min);
    const maxY = positionScale(fixedMax);

    substitutionMesh.uniforms.setMinY(minY).setMaxY(maxY);
    deletionMesh.uniforms.setMinY(minY).setMaxY(maxY);
    additionMesh.uniforms.setMinY(minY).setMaxY(maxY);
    selectionsMesh.uniforms.setMinY(minY).setMaxY(maxY);
  }

  function onFiltersChange(updatedFilters) {
    state.filters = updatedFilters;

    track.setFilters([...state.filters]);

    setTimeout(() => {
      calcFilters();
      applyFilters();
      adjustAxis();
      update();
      track.setFilters([...state.filters]);
    }, 50);
  }

  function applyFilters() {
    const activeFilters = getActiveFilters();
    const hasFilters = Boolean(activeFilters.length);
    applyFiltersToVariants(state.allVariants, activeFilters);
    updateVis(state.substitutions, substitutionMesh, hasFilters, 0.05);
    updateVis(state.deletions, deletionMesh, hasFilters, 0.05);
    updateVis(state.additions, additionMesh, hasFilters, 0);
    updateSelection();
    update();
  }

  function getActiveFilters() {
    return state.filters.filter((x) => frequencyDbs[x.key] && x.value);
  }

  function applyFiltersToVariants(variants, activeFilters) {
    if (!activeFilters.length) return;

    const dict = activeFilters
      .map((x) => x.key)
      .reduce((a, i) => ({ ...a, [i]: true }), {});

    for (let i = 0; i < variants.length; i++) {
      const v = variants[i];
      v.hidden = !dict[v.db.key];
    }
  }

  function onFilterHighlight(key) {
    updateColorMap(key);
  }

  function updateSelection() {
    const activeFilters = getActiveFilters();
    const hasFilters = Boolean(activeFilters.length);
    const { selectedVariantId } = track.getSharedState();
    selectionsMesh.reset();

    state.selections = dbs
      .map((x) => x.index)
      .map((x) => `${x}-${selectedVariantId}`)
      .map((x) => state.variantDict[x])
      .filter(Boolean);

    selectionsMesh.setItems(state.selections);
    drawSelection(state.selections, selectionsMesh, hasFilters, 0.05);
  }

  function onSharedStateChange() {
    updateSelection();
    update();
  }

  return {
    onReset,
    onDataReceived,
    onRangeChange,
    onSharedStateChange,
    onFilterHighlight,
    onInit,
    onFiltersChange,
    onPreferencesChanged,
    meshes: [substitutionMesh, deletionMesh, additionMesh, selectionsMesh],
  };
}

export default getVariantsTrackController;
